// IfsProcessingVM.js
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import * as XLSX from "xlsx";
import {
  uploadAutomationData,
  processAutomationData,
  downloadProcessedData,
  checkCountData,
  checkAndDeleteFileFromS3,
  translateRecords,
  getAllLanguages,
} from "../model/IfsProcessingModel";

import { v4 as uuidv4 } from "uuid";

const DataProcessingVM = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleToastOpen } = useToast();
  const uploadIntervalRef = useRef(null);
  const translationCheckIntervalRef = useRef(null);
  const translationProgressIntervalRef = useRef(null);
  const [duplicateDetails, setDuplicateDetails] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  const [activeStep, setActiveStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isProcessingComplete, setIsProcessingComplete] = useState(false);
  const [selectedModel, setSelectedModel] = useState("");
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingStats, setProcessingStats] = useState({
    processed: 0,
    total: 5000,
    timeElapsed: "0:00",
    timeRemaining: "4:30",
  });
  const [isMobile, setIsMobile] = useState(false);
  const [uniqueRecords, setUniqueRecords] = useState(0);
  const [processRecords, setProcessRecords] = useState(0);
  const [batchId, setBatchId] = useState(null);
  const [cOpen, setCOpen] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [data, setData] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [checkCountTimer, setCheckCountTimer] = useState(null);
  const [modelOptions, setModelOptions] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [managedLanguages, setManagedLanguages] = useState([]);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState(0);
  const [translationComplete, setTranslationComplete] = useState(false);
  const [translationStats, setTranslationStats] = useState({
    processed: 0,
    total: 0,
    percentage: 0,
  });
  const [showTranslationModal, setShowTranslationModal] = useState(false);
  const [allLanguagesFromApi, setAllLanguagesFromApi] = useState([]);

  const selectedTool = "IFS";

  const HARDCODED_DEFAULT_LANGUAGES = [
    { id: 999001, name: "FRENCH", isHardcoded: true },
    { id: 999002, name: "SPANISH", isHardcoded: true },
    { id: 999003, name: "PORTUGUESE", isHardcoded: true },
    { id: 999004, name: "CROATIAN", isHardcoded: true },
  ];

  const DEFAULT_LANGUAGE_IDS = [999001, 999002, 999003, 999004];
  const MAX_TOTAL_LANGUAGES = 6;
  const STORAGE_KEY = "ifs_additional_languages";

  const calculateTimings = (recordCount) => {
    const count = recordCount || 0;

    let translationDuration, checkCountDuration;

    if (count <= 500) {
      translationDuration = 1 * 60 * 1000;
      checkCountDuration = 10 * 1000;
    } else if (count <= 1000) {
      translationDuration = 1.5 * 60 * 1000;
      checkCountDuration = 15 * 1000;
    } else if (count <= 2500) {
      translationDuration = 2 * 60 * 1000;
      checkCountDuration = 20 * 1000;
    } else if (count <= 5000) {
      translationDuration = 3 * 60 * 1000;
      checkCountDuration = 25 * 1000;
    } else if (count <= 10000) {
      translationDuration = 4 * 60 * 1000;
      checkCountDuration = 30 * 1000;
    } else if (count <= 15000) {
      translationDuration = 5 * 60 * 1000;
      checkCountDuration = 30 * 1000;
    } else if (count <= 20000) {
      translationDuration = 6 * 60 * 1000;
      checkCountDuration = 40 * 1000;
    } else {
      translationDuration = 7 * 60 * 1000;
      checkCountDuration = 50 * 1000;
    }

    const totalDuration = translationDuration + checkCountDuration;

    return {
      translationDuration,
      checkCountDuration,
      totalDuration,
    };
  };

  const loadAdditionalLanguagesFromStorage = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error("Error loading from localStorage:", error);
    }
    return [];
  };

  const saveAdditionalLanguagesToStorage = (languageIds) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(languageIds));
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  };

  useEffect(() => {
    const loadAllLanguages = async () => {
      setIsLoadingOptions(true);
      try {
        let apiLanguages = [];
        try {
          apiLanguages = await getAllLanguages();
        } catch (error) {
          console.error("Error fetching languages from API:", error);
          apiLanguages = [];
        }

        setAllLanguagesFromApi(apiLanguages);

        const storedAdditionalLanguageIds =
          loadAdditionalLanguagesFromStorage();

        const additionalLanguages = apiLanguages.filter((lang) =>
          storedAdditionalLanguageIds.includes(lang.id),
        );

        const allSelectedLanguages = [
          ...HARDCODED_DEFAULT_LANGUAGES,
          ...additionalLanguages,
        ];

        setManagedLanguages(allSelectedLanguages);
        setModelOptions(allSelectedLanguages);

        const allSelectedIds = allSelectedLanguages.map((lang) => lang.id);
        const availableLangs = apiLanguages.filter(
          (lang) => !allSelectedIds.includes(lang.id),
        );
        setAvailableLanguages(availableLangs);
      } catch (error) {
        console.error("Error loading languages:", error);
        handleToastOpen("error", "Failed to load languages");
        setAvailableLanguages([]);
        setModelOptions(HARDCODED_DEFAULT_LANGUAGES);
        setManagedLanguages(HARDCODED_DEFAULT_LANGUAGES);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    loadAllLanguages();
  }, []);

  const cardData = [
    {
      header: "Unique Records",
      value: `${uniqueRecords}/${totalCount || 0}`,
      id: "unique",
    },
    {
      header: "Processed Records",
      value: `${processRecords}/${totalCount || 0}`,
      id: "process",
    },
  ];

  const clearAllIntervals = () => {
    if (uploadIntervalRef.current) {
      clearInterval(uploadIntervalRef.current);
      uploadIntervalRef.current = null;
    }

    if (checkCountTimer) {
      clearTimeout(checkCountTimer);
      setCheckCountTimer(null);
    }

    if (translationCheckIntervalRef.current) {
      clearTimeout(translationCheckIntervalRef.current);
      translationCheckIntervalRef.current = null;
    }

    if (translationProgressIntervalRef.current) {
      cancelAnimationFrame(translationProgressIntervalRef.current);
      translationProgressIntervalRef.current = null;
    }
  };

  const handleManageLanguages = () => {
    setShowLanguageModal(true);
  };

  const isDefaultLanguage = (languageId) => {
    return DEFAULT_LANGUAGE_IDS.includes(languageId);
  };

  const handleAddLanguage = (languageId) => {
    if (managedLanguages.length >= MAX_TOTAL_LANGUAGES) {
      handleToastOpen(
        "error",
        `You can only have up to ${MAX_TOTAL_LANGUAGES} languages in total`,
      );
      return;
    }

    const newLanguage = allLanguagesFromApi.find(
      (lang) => lang.id === parseInt(languageId),
    );

    if (
      newLanguage &&
      !managedLanguages.some((lang) => lang.id === newLanguage.id)
    ) {
      const updatedLanguages = [...managedLanguages, newLanguage];
      setManagedLanguages(updatedLanguages);
      setModelOptions(updatedLanguages);

      const additionalLanguageIds = updatedLanguages
        .filter((lang) => !isDefaultLanguage(lang.id))
        .map((lang) => lang.id);

      saveAdditionalLanguagesToStorage(additionalLanguageIds);

      const updatedAvailableLanguages = availableLanguages.filter(
        (lang) => lang.id !== newLanguage.id,
      );
      setAvailableLanguages(updatedAvailableLanguages);

      handleToastOpen("success", `${newLanguage.name} added successfully`);
    }
  };

  const handleDeleteLanguage = (languageId) => {
    const languageToDelete = managedLanguages.find(
      (lang) => lang.id === parseInt(languageId),
    );

    if (languageToDelete && isDefaultLanguage(languageToDelete.id)) {
      handleToastOpen("error", "Default languages cannot be deleted");
      return;
    }

    const updatedLanguages = managedLanguages.filter(
      (lang) => lang.id !== parseInt(languageId),
    );
    setManagedLanguages(updatedLanguages);
    setModelOptions(updatedLanguages);

    const additionalLanguageIds = updatedLanguages
      .filter((lang) => !isDefaultLanguage(lang.id))
      .map((lang) => lang.id);

    saveAdditionalLanguagesToStorage(additionalLanguageIds);

    if (languageToDelete && !isDefaultLanguage(languageToDelete.id)) {
      const updatedAvailableLanguages = [
        ...availableLanguages,
        languageToDelete,
      ].sort((a, b) => a.name.localeCompare(b.name));
      setAvailableLanguages(updatedAvailableLanguages);
    }

    if (languageToDelete) {
      handleToastOpen(
        "success",
        `${languageToDelete.name} deleted successfully`,
      );
    }
  };

  const handleStartTranslation = async () => {
    if (!batchId || !selectedModel) {
      handleToastOpen("error", "Missing batch ID or language selection");
      return;
    }

    setIsTranslating(true);
    setShowTranslationModal(true);
    setTranslationProgress(0);

    const timings = calculateTimings(totalCount);
    const { translationDuration, checkCountDuration, totalDuration } = timings;

    console.log(`Translation timings for ${totalCount} records:`, {
      translationDuration: `${translationDuration / 1000}s`,
      checkCountDuration: `${checkCountDuration / 1000}s`,
      totalDuration: `${totalDuration / 1000}s`,
    });

    const translationStartTime = Date.now();

    const formatTime = (ms) => {
      const totalSeconds = Math.floor(ms / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    };

    setTranslationStats({
      processed: 0,
      total: totalCount || 0,
      percentage: 0,
      timeElapsed: "0:00",
      timeRemaining: formatTime(totalDuration),
    });

    try {
      console.log(
        "Triggering translation for batch:",
        batchId,
        "language:",
        selectedModel,
      );

      const translationTimeout = 10000;
      const translationPromise = translateRecords(
        batchId,
        selectedModel,
        translationTimeout,
      )
        .then((response) => ({ success: true, response }))
        .catch((error) => {
          if (
            error.response?.status === 504 ||
            error.code === "ECONNABORTED" ||
            error.message?.includes("timeout")
          ) {
            console.log(
              "Translation API timed out (expected) - continuing with polling",
            );
            return { timedOut: true };
          } else {
            throw error;
          }
        });

      const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => resolve({ timedOut: true }), translationTimeout);
      });

      await Promise.race([translationPromise, timeoutPromise]);

      handleToastOpen("info", "Translation started. Please wait...");

      const updateProgress = () => {
        const elapsed = Date.now() - translationStartTime;
        const totalProgress = Math.min(100, (elapsed / totalDuration) * 100);
        setTranslationProgress(totalProgress);

        const elapsedSeconds = Math.floor(elapsed / 1000);
        const elapsedMinutes = Math.floor(elapsedSeconds / 60);
        const timeElapsed = `${elapsedMinutes}:${(elapsedSeconds % 60).toString().padStart(2, "0")}`;

        const remainingMs = Math.max(0, totalDuration - elapsed);
        const remainingSeconds = Math.floor(remainingMs / 1000);
        const remainingMinutes = Math.floor(remainingSeconds / 60);
        const timeRemaining = `${remainingMinutes}:${(remainingSeconds % 60).toString().padStart(2, "0")}`;

        let estimatedProcessed = 0;
        if (elapsed < translationDuration) {
          estimatedProcessed = Math.floor(
            (totalCount || 0) * (elapsed / translationDuration) * 0.9,
          );
        } else {
          const checkCountElapsed = elapsed - translationDuration;
          const checkCountProgress = checkCountElapsed / checkCountDuration;
          estimatedProcessed = Math.floor(
            (totalCount || 0) * (0.9 + checkCountProgress * 0.1),
          );
        }

        setTranslationStats((prev) => ({
          ...prev,
          processed: Math.min(estimatedProcessed, totalCount || 0),
          timeElapsed,
          timeRemaining,
          percentage: Number(totalProgress.toFixed(1)),
        }));

        if (elapsed < totalDuration) {
          translationProgressIntervalRef.current =
            requestAnimationFrame(updateProgress);
        }
      };

      translationProgressIntervalRef.current =
        requestAnimationFrame(updateProgress);

      setTimeout(() => {
        console.log(
          `Translation phase complete (${translationDuration / 1000}s), starting checkCount phase`,
        );
        handleToastOpen(
          "info",
          "Initial translation completed. Checking progress...",
        );
      }, translationDuration);

      setTimeout(async () => {
        if (translationProgressIntervalRef.current) {
          cancelAnimationFrame(translationProgressIntervalRef.current);
          translationProgressIntervalRef.current = null;
        }

        try {
          console.log("Calling checkCountData API");
          const params = new URLSearchParams(location.search);
          const urlBatchId = params.get("batchId");
          const urlModelName = params.get("modelName");
          const urlToBeProcessed = params.get("toBeProcessed") === "true";

          const modelToUse = urlToBeProcessed ? urlModelName : selectedModel;
          const statusResponse = await checkCountData(
            data,
            selectedTool,
            modelToUse,
          );

          console.log("Check count API response:", statusResponse);

          if (
            statusResponse &&
            statusResponse.records_processed !== undefined
          ) {
            setProcessRecords(statusResponse.records_processed);
            setTranslationStats((prev) => ({
              ...prev,
              processed: statusResponse.records_processed,
            }));
          }

          setTranslationProgress(100);
          setTranslationComplete(true);
          handleToastOpen("success", "Translation completed successfully!");
        } catch (err) {
          console.error("Error checking count:", err);
          handleToastOpen("error", "Error checking translation progress");
        } finally {
          setIsTranslating(false);
          setTimeout(() => {
            setShowTranslationModal(false);
          }, 2000);
        }
      }, totalDuration);
    } catch (error) {
      console.error("Translation error:", error);
      clearAllIntervals();
      setIsTranslating(false);
      setShowTranslationModal(false);
      handleToastOpen("error", `Translation error: ${error.message}`);
    }
  };

  const handleFileUpload = async () => {
    try {
      clearAllIntervals();

      setIsUploading(true);
      setUploadProgress(0);
      await new Promise((resolve) => setTimeout(resolve, 500));

      const startTime = Date.now();

      let uploadDuration;
      if (totalCount <= 1000) {
        uploadDuration = 10000;
      } else if (totalCount <= 5000) {
        uploadDuration = 20000;
      } else if (totalCount <= 10000) {
        uploadDuration = 30000;
      } else if (totalCount <= 15000) {
        uploadDuration = 40000;
      } else {
        uploadDuration = 50000;
      }

      uploadIntervalRef.current = setInterval(() => {
        const elapsedMs = Date.now() - startTime;
        const progressPercentage = Math.min(
          90,
          (elapsedMs / uploadDuration) * 100,
        );
        setUploadProgress((prevProgress) =>
          Math.max(prevProgress, Math.floor(progressPercentage)),
        );

        const elapsedSeconds = Math.floor(elapsedMs / 1000);
        const elapsedMinutes = Math.floor(elapsedSeconds / 60);
        const remainingSeconds = Math.floor(
          (uploadDuration - elapsedMs) / 1000,
        );
        const remainingMinutes = Math.floor(remainingSeconds / 60);

        setProcessingStats({
          processed: Math.floor((totalCount || 0) * (progressPercentage / 100)),
          total: totalCount || 0,
          timeElapsed: `${elapsedMinutes}:${(elapsedSeconds % 60).toString().padStart(2, "0")}`,
          timeRemaining: `${remainingMinutes}:${(remainingSeconds % 60).toString().padStart(2, "0")}`,
        });
      }, 800);

      try {
        await validateFile(selectedFile);
      } catch (validationError) {
        clearAllIntervals();
        setIsUploading(false);
        handleToastOpen("error", validationError.message || validationError);
        return;
      }

      let currentUniqueRecords = uniqueRecords;
      let currentTotalCount = totalCount;

      if (!currentUniqueRecords || currentUniqueRecords === 0) {
        try {
          currentUniqueRecords = await countUniqueRecords(selectedFile);
        } catch (error) {
          clearAllIntervals();
          setIsUploading(false);
          handleToastOpen("error", "Failed to count unique records");
          return;
        }
      }

      if (!currentTotalCount || currentTotalCount === 0) {
        try {
          const totalCountResult = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                  header: 1,
                });
                const rowsWithoutHeader = jsonData
                  .slice(1)
                  .filter((row) => row.length > 0);
                setTotalCount(rowsWithoutHeader.length);
                resolve(rowsWithoutHeader.length);
              } catch (error) {
                reject(error);
              }
            };
            reader.onerror = () => reject("Error reading file");
            reader.readAsArrayBuffer(selectedFile);
          });

          currentTotalCount = totalCountResult;
        } catch (error) {
          clearAllIntervals();
          setIsUploading(false);
          handleToastOpen("error", "Failed to calculate total record count");
          return;
        }
      }

      const fileArray = [selectedFile];
      const modelToUse = selectedModel;
      const batchIdToUse = uuidv4();
      setBatchId(batchIdToUse);

      const response = await uploadAutomationData(
        fileArray,
        selectedTool,
        modelToUse,
        currentUniqueRecords,
        currentTotalCount,
        batchIdToUse,
      );

      clearAllIntervals();
      setUploadProgress(100);

      setTimeout(() => {
        setIsUploading(false);

        // if (response?.status === 201) {
        //   setData(response);
        //   const folderId = response.data[0].folderId;
        //   setCurrentFolderId(folderId);
        //   const currentElapsedTime = processingStats.timeElapsed || "0:00";

        //   setProcessingStats({
        //     processed: totalCount,
        //     total: totalCount,
        //     timeElapsed: currentElapsedTime,
        //     timeRemaining: "0:00"
        //   });

        //   handleToastOpen("success", "File Uploaded Successfully");
        //   setFileUploaded(true);
        //   setActiveStep(2);
        if (response?.status === 201) {
          setData(response);
          const folderId = response.data[0].folderId;
          const currentElapsedTime = processingStats.timeElapsed || "0:00";

          setProcessingStats({
            processed: totalCount,
            total: totalCount,
            timeElapsed: currentElapsedTime,
            timeRemaining: "0:00",
          });

          // ── duplicate check ──────────────────────────────────────────
          const dupCheck = response.apiResponse?.duplicate_check;
          if (dupCheck?.has_duplicates) {
            setDuplicateDetails(dupCheck);
            setShowDuplicateModal(true);
          }
          // ─────────────────────────────────────────────────────────────

          handleToastOpen("success", "File Uploaded Successfully");
          setFileUploaded(true);
          setActiveStep(2);
        } else {
          handleToastOpen(
            "error",
            "Upload failed with status: " + (response?.status || "unknown"),
          );
        }
      }, 500);
    } catch (error) {
      clearAllIntervals();

      let errorDetails = {};

      const errorData =
        error &&
        error.message &&
        (error.missing_columns || error.expected_columns || error.found_columns)
          ? error
          : error.response && error.response.data && error.response.data.detail
            ? error.response.data.detail
            : null;

      if (errorData) {
        const tryDecodeBase64 = (str) => {
          if (typeof str !== "string") return str;

          if (/^[A-Za-z0-9+/=]+$/.test(str) && str.length > 20) {
            try {
              const decoded = atob(str);
              return decoded;
            } catch (e) {
              return str;
            }
          }
          return str;
        };

        const processArray = (arr) => {
          if (!Array.isArray(arr)) return [];
          return arr.map((item) => tryDecodeBase64(item));
        };

        errorDetails = {
          message: errorData.message || "Invalid file format",
          missing_columns: processArray(errorData.missing_columns || []),
          expected_columns: processArray(errorData.expected_columns || []),
          extra_columns: processArray(errorData.extra_columns || []),
          found_columns: processArray(errorData.found_columns || []),
        };

        setErrorDetails(errorDetails);
        setShowErrorModal(true);
      } else {
        const errorMessage =
          error.message || "Something went wrong, please try again later!";
        handleToastOpen("error", errorMessage);
      }
      setIsUploading(false);
    }
  };

  const handleStartProcessing = async () => {
    try {
      const params = new URLSearchParams(location.search);
      const urlBatchId = params.get("batchId");
      const urlModelName = params.get("modelName");
      const urlTotalRecords = params.get("totalRecords");
      const urlToBeProcessed = params.get("toBeProcessed") === "true";

      clearAllIntervals();

      setIsProcessing(true);
      setProcessingProgress(0);

      const recordCount =
        totalCount || (urlTotalRecords ? parseInt(urlTotalRecords, 10) : 0);

      let processingDuration;
      if (recordCount <= 1000) {
        processingDuration = 1.5 * 60 * 1000;
      } else if (recordCount <= 5000) {
        processingDuration = 2.5 * 60 * 1000;
      } else if (recordCount <= 10000) {
        processingDuration = 3 * 60 * 1000;
      } else if (recordCount <= 15000) {
        processingDuration = 3.5 * 60 * 1000;
      } else {
        processingDuration = 4 * 60 * 1000;
      }

      setProcessingStats({
        processed: 0,
        total: recordCount,
        timeElapsed: "0:00",
        timeRemaining: "calculating...",
      });

      const startTime = Date.now();

      const progressInterval = setInterval(() => {
        const elapsedMs = Date.now() - startTime;
        const adjustedElapsedMs = Math.max(0, elapsedMs - 8000);
        const elapsedMinutes = Math.floor(adjustedElapsedMs / 60000);
        const elapsedSeconds = Math.floor((adjustedElapsedMs % 60000) / 1000);
        const timeElapsed = `${elapsedMinutes}:${elapsedSeconds < 10 ? "0" + elapsedSeconds : elapsedSeconds}`;

        const currentProgress = Math.min(
          90,
          Math.floor((elapsedMs / processingDuration) * 100),
        );
        setProcessingProgress((prevProgress) =>
          Math.max(prevProgress, currentProgress),
        );

        const estimatedProcessed = Math.floor(
          (recordCount * currentProgress) / 100,
        );

        const remainingMs = processingDuration - elapsedMs;
        const adjustedRemainingMs = remainingMs + 8000;
        const remainingMinutes = Math.floor(adjustedRemainingMs / 60000);
        const remainingSeconds = Math.floor(
          (adjustedRemainingMs % 60000) / 1000,
        );
        const timeRemaining = `${remainingMinutes}:${remainingSeconds < 10 ? "0" + remainingSeconds : remainingSeconds}`;

        setProcessingStats({
          processed: estimatedProcessed,
          total: recordCount,
          timeElapsed: timeElapsed,
          timeRemaining: timeRemaining,
        });
      }, 1000);

      uploadIntervalRef.current = progressInterval;

      let hasCompletedProcessing = false;
      let processResponse;
      let processData = data;

      try {
        if (urlToBeProcessed && urlBatchId) {
          const baseFolderPath = "ifs/";
          const mockFileName = `IFS_Operations_${urlModelName.replace(/\s+/g, "_")}.xlsx`;
          const key = `${baseFolderPath}${urlBatchId}/input_file.xlsx`;
          const s3Url = `https://${process.env.REACT_APP_UAT_AWS_BUCKET}.s3.amazonaws.com/${key}`;

          const uploadResults = [
            {
              originalFile: mockFileName,
              fileSize: 0,
              fileType:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              s3Location: s3Url,
              key: key,
              folderId: urlBatchId,
              activityType: selectedTool,
            },
          ];

          processData = {
            status: 201,
            data: uploadResults,
          };

          setData(processData);
        }

        const modelToUse = urlToBeProcessed ? urlModelName : selectedModel;

        try {
          const processingBatchIdToUse = batchId || uuidv4();
          setBatchId(processingBatchIdToUse);

          processResponse = await processAutomationData(
            selectedTool,
            processData,
            modelToUse,
            totalCount,
            processingBatchIdToUse,
          );
        } catch (processError) {
          console.error("Processing error:", processError);
        }

        const pollingStartTime = Date.now();

        const checkProcessingStatus = async () => {
          if (hasCompletedProcessing) {
            return;
          }

          try {
            const elapsedTime = Date.now() - pollingStartTime;

            let interval;
            if (recordCount <= 1000) {
              interval = 3000;
            } else if (recordCount <= 5000) {
              interval = 5000;
            } else if (recordCount <= 10000) {
              interval = 7000;
            } else if (recordCount <= 15000) {
              interval = 10000;
            } else {
              interval = 15000;
            }

            const modelToUse = urlToBeProcessed ? urlModelName : selectedModel;
            const fileModelResponse = await checkCountData(
              processData,
              selectedTool,
              modelToUse,
            );

            if (fileModelResponse.status === "completed") {
              hasCompletedProcessing = true;
              clearAllIntervals();

              setBatchId(fileModelResponse.batch_id);
              setProcessRecords(fileModelResponse.records_processed);

              const originalFileName =
                processData.data?.[0]?.originalFile || "";
              const folderIdToUse = urlToBeProcessed
                ? urlBatchId
                : currentFolderId;

              await checkAndDeleteFileFromS3(
                fileModelResponse,
                folderIdToUse,
                processData,
                null,
                originalFileName,
              );

              setProcessingProgress(100);
              setProcessingStats((prevStats) => ({
                ...prevStats,
                processed: prevStats.total,
                timeRemaining: "0:00",
              }));

              setTimeout(() => {
                setIsProcessing(false);
                setIsProcessingComplete(true);
                setActiveStep(3);
                handleToastOpen("success", "Processing complete!");

                if (selectedModel && selectedModel.trim() !== "") {
                  setTimeout(() => {
                    handleStartTranslation();
                  }, 1000);
                }
              }, 500);
            } else {
              const timerId = setTimeout(
                () => checkProcessingStatus(),
                interval,
              );
              setCheckCountTimer(timerId);
            }
          } catch (error) {
            console.error("Error checking count data:", error);
            const timerId = setTimeout(() => checkProcessingStatus(), 10000);
            setCheckCountTimer(timerId);
          }
        };

        checkProcessingStatus();
      } catch (setupError) {
        console.error("Setup error:", setupError);
        clearAllIntervals();
        setIsProcessing(false);
        handleToastOpen(
          "error",
          setupError.message || "Error setting up processing",
        );
      }

      setFileUploaded(false);
    } catch (error) {
      console.error("Processing error:", error);
      clearAllIntervals();
      setIsProcessing(false);
      handleToastOpen("error", error.message || "Error during processing");
    }
  };

  useEffect(() => {
    return () => {
      clearAllIntervals();
    };
  }, []);

  // const handleDownload = async () => {
  //   if (!isProcessingComplete) return;

  //   setCOpen(true);
  //   try {
  //     const modelForDownload = selectedModel;

  //     const downloadData = await downloadProcessedData(
  //       selectedTool,
  //       batchId,
  //       modelForDownload,
  //       translationComplete
  //     );

  //     const formatName = (name) => {
  //       return name ? name.replace(/[\s-]+/g, '_') : '';
  //     };

  //     const formattedModel = formatName(modelForDownload);
  //     const formattedTime = new Date().toISOString().replace(/[-:]/g, '').slice(0, 15).replace('T', '_');

  //     const filename = translationComplete
  //       ? `IFS_Operations_${formattedModel}_Translated_${formattedTime}.xlsx`
  //       : `IFS_Operations_${formattedModel}_${formattedTime}.xlsx`;

  //     const binaryString = atob(downloadData.base64Data);
  //     const bytes = new Uint8Array(binaryString.length);
  //     for (let i = 0; i < binaryString.length; i++) {
  //       bytes[i] = binaryString.charCodeAt(i);
  //     }
  //     const blob = new Blob([bytes], {
  //       type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  //     });

  //     const downloadUrl = URL.createObjectURL(blob);
  //     const link = document.createElement('a');
  //     link.href = downloadUrl;
  //     link.download = filename;
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);

  //     setTimeout(() => {
  //       URL.revokeObjectURL(downloadUrl);
  //     }, 100);

  //     handleToastOpen("success", "Excel File Downloaded Successfully");

  //     setTimeout(() => {
  //       handleViewHistory();
  //     }, 500);

  //   } catch (error) {
  //     if (error.name !== 'AbortError') {
  //       handleToastOpen("error", "Excel File Download Failed: " + (error.message || "Unknown error"));
  //     }
  //   } finally {
  //     setCOpen(false);
  //   }
  // };

  const handleDownload = async () => {
    if (!isProcessingComplete) return;

    setCOpen(true);
    try {
      // downloadProcessedData now handles everything including
      // triggering the browser download internally
      const { filename } = await downloadProcessedData(
        selectedTool,
        batchId,
        selectedModel,
        translationComplete,
      );

      handleToastOpen("success", `Downloaded: ${filename}`);

      setTimeout(() => {
        handleViewHistory();
      }, 500);
    } catch (error) {
      if (error.name !== "AbortError") {
        handleToastOpen(
          "error",
          "Download failed: " + (error.message || "Unknown error"),
        );
      }
    } finally {
      setCOpen(false);
    }
  };

  const handleReset = () => {
    const params = new URLSearchParams(location.search);
    const urlToBeProcessed = params.get("toBeProcessed") === "true";

    if (urlToBeProcessed) {
      navigate(-1);
    } else {
      setSelectedFile(null);
      setFileUploaded(false);
      setIsProcessingComplete(false);
      setActiveStep(1);
      setUniqueRecords(0);
      setProcessRecords(0);
      setTotalCount(0);
      setIsTranslating(false);
      setTranslationComplete(false);
      setTranslationProgress(0);
      setShowTranslationModal(false);
      handleToastOpen("Process reset", "");
    }
  };

  const handleModelChange = (event) => {
    setSelectedModel(event);
  };

  const handleViewHistory = () => {
    navigate(
      `/ifs-operation-centre/history?tool=${encodeURIComponent(selectedTool)}`,
    );
  };

  const countUniqueRecords = async (file) => {
    if (!file) {
      return 0;
    }

    const fileObject = Array.isArray(file) ? file[0] : file;

    if (!fileObject) {
      return 0;
    }

    const fileType = fileObject.type;
    const fileName = fileObject.name || "";

    const isExcel =
      fileType ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      fileName.endsWith(".xlsx") ||
      fileName.endsWith(".xls");

    if (!isExcel) {
      return 0;
    }

    const reader = new FileReader();

    return new Promise((resolve, reject) => {
      reader.onload = function (event) {
        try {
          const workbook = XLSX.read(event.target.result, {
            type: "array",
            cellDates: true,
          });

          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];

          const data = XLSX.utils.sheet_to_json(sheet, {
            defval: null,
            blankrows: false,
          });

          let uniqueCount = data.length > 0 ? data.length : 0;

          setUniqueRecords(uniqueCount);
          resolve(uniqueCount);
        } catch (error) {
          console.error("Error processing file:", error);
          reject(error);
        }
      };

      reader.onerror = function () {
        reject("FileReader error");
      };

      try {
        reader.readAsArrayBuffer(fileObject);
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleFileSelection = async (files) => {
    if (!files || files.length === 0) return;

    const file = files[0];

    const validMimeTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    const validExtensions = ["xlsx", "xls"];
    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (
      !validMimeTypes.includes(file.type) ||
      !validExtensions.includes(fileExtension)
    ) {
      handleToastOpen(
        "error",
        "Invalid file type. Please upload an Excel (.xlsx or .xls) file.",
      );
      return;
    }

    setErrorDetails(null);
    setShowErrorModal(false);

    setSelectedFile(file);
    setCOpen(true);

    try {
      const recordLimit = 100000;

      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheet = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheet];

          const allRows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          const rowsWithoutHeader = allRows
            .slice(1)
            .filter((row) => row.length > 0);
          const recordCount = rowsWithoutHeader.length;

          if (recordCount > recordLimit) {
            setSelectedFile("");
            handleToastOpen(
              "error",
              `File contains ${recordCount} records. Maximum allowed is ${recordLimit}.`,
            );
          } else {
            setTotalCount(recordCount);
            handleToastOpen("success", `Selected file: ${file.name}`);
          }
        } catch (err) {
          handleToastOpen("error", `Error processing file: ${err.message}`);
        } finally {
          setCOpen(false);
        }
      };

      reader.onerror = () => {
        handleToastOpen("error", "Error reading file.");
        setCOpen(false);
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      handleToastOpen("error", error.message);
      setCOpen(false);
    }
  };

  const validateFile = (file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject("No file selected.");
        return;
      }

      const validMimeTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];

      const validExtensions = ["xlsx", "xls"];
      const fileExtension = file.name.split(".").pop().toLowerCase();

      if (!validMimeTypes.includes(file.type)) {
        reject(
          "Invalid file type. Please upload an Excel (.xlsx or .xls) file.",
        );
        return;
      }

      if (!validExtensions.includes(fileExtension)) {
        reject(
          "Invalid file extension. Please upload an Excel (.xlsx or .xls) file.",
        );
        return;
      }

      const recordLimit = 100000;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          const recordCount = jsonData.length;

          if (recordCount > recordLimit) {
            reject(
              `File contains ${recordCount} records. Maximum allowed is ${recordLimit} records.`,
            );
            return;
          }

          resolve(null);
        } catch (error) {
          reject(`Error processing file: ${error.message}`);
        }
      };

      reader.onerror = () => {
        reject("Error reading file.");
      };

      reader.readAsArrayBuffer(file);
    });
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const params = new URLSearchParams(location.search);
  const urlBatchId = params.get("batchId");
  const urlModelName = params.get("modelName");
  const urlTotalRecords = params.get("totalRecords")
    ? parseInt(params.get("totalRecords"), 10)
    : null;
  const urlUniqueRecords = params.get("uniqueRecords")
    ? parseInt(params.get("uniqueRecords"), 10)
    : null;
  const urlToBeProcessed = params.get("toBeProcessed") === "true";

  useEffect(() => {
    if (urlBatchId && urlToBeProcessed) {
      setBatchId(urlBatchId);
      setActiveStep(2);
      setFileUploaded(true);

      if (urlTotalRecords) {
        setTotalCount(urlTotalRecords);
      }

      if (urlUniqueRecords) {
        setUniqueRecords(urlUniqueRecords);
      }

      if (urlModelName) {
        setSelectedModel(urlModelName);
      }

      handleToastOpen("success", "File Retrieved Successfully");
    }
  }, []);

  const handleErrorDialogClose = () => {
    setShowErrorModal(false);
    setErrorDetails(null);
  };

  const handleRequestSample = () => {
    downloadSampleFile();
  };

  const downloadSampleFile = () => {
    const data = [
      {
        Material: "7000000458",
        Pl: "",
        "Material Description": "Cover for odu plug 7114-0120-0110",
        "Long Description": "7114-0120-0110 PLASTIC COVER FOR PLUG 309.021",
        "Equipment Function": "SP - ELECTRICAL",
        "Material Criticality Indicator": "",
        Language: "E",
        "Matl Group": "EN",
        uom: "ISPR68",
        PGr: "EA",
        MTyp: "HWR",
        "Valuation class": "INBW",
        "Part Type Code": "3300",
        "Level 1": "PT113",
        "Level 2": "CONSUMABLES",
        "Level 3": "ELECTRICAL CONSUMABLES",
        "Level 4": "MISC. ELECTRICAL POWER CABLE COVER",
      },
      {
        Material: "7000001517",
        Pl: "",
        "Material Description": "15A Electrical Socket",
        "Long Description": "15A ELECTRICAL SOCKET SWITCH",
        "Equipment Function": "",
        "Material Criticality Indicator": "",
        Language: "E",
        "Matl Group": "EN",
        uom: "ISPR68",
        PGr: "EA",
        MTyp: "HWR",
        "Valuation class": "INBW",
        "Part Type Code": "3300",
        "Level 1": "PT113",
        "Level 2": "CONSUMABLES",
        "Level 3": "ELECTRICAL CONSUMABLES",
        "Level 4": "MISC. ELECTRICAL PLUG",
      },
    ];
    const filename = "IFS_Operations_Input_Sample.xlsx";
    try {
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sample Data");
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      handleToastOpen("success", `Sample template "${filename}" downloaded`);
    } catch (error) {
      handleToastOpen("error", "Failed to generate sample template");
    }
  };

  const handleCloseTranslationModal = () => {
    if (!isTranslating) {
      setShowTranslationModal(false);
    }
  };

  return {
    activeStep,
    selectedFile,
    fileUploaded,
    isProcessing,
    isProcessingComplete,
    selectedModel,
    processingProgress,
    processingStats,
    isMobile,
    totalCount,
    isUploading,
    uploadProgress,
    batchId,
    errorDetails,
    showErrorModal,
    modelOptions,
    cardData,
    cOpen,
    isLoadingOptions,
    showLanguageModal,
    managedLanguages,
    availableLanguages,
    isTranslating,
    translationProgress,
    translationComplete,
    translationStats,
    showTranslationModal,
    setActiveStep,
    setSelectedFile,
    setFileUploaded,
    setIsProcessing,
    setIsProcessingComplete,
    setSelectedModel,
    setProcessingProgress,
    setProcessingStats,
    setCOpen,
    setShowLanguageModal,
    handleFileUpload,
    handleStartProcessing,
    handleDownload,
    handleRequestSample,
    handleReset,
    handleModelChange,
    handleViewHistory,
    handleFileSelection,
    handleErrorDialogClose,
    handleManageLanguages,
    handleAddLanguage,
    handleDeleteLanguage,
    handleStartTranslation,
    handleCloseTranslationModal,
    clearAllIntervals,
    // ...all existing returns...
  duplicateDetails,
  showDuplicateModal,
  handleCloseDuplicateModal: () => setShowDuplicateModal(false),
  };
};

export default DataProcessingVM;
